import { useEffect, useRef, useState } from 'react';
import { getSafeSupabaseClient, getCurrentWorkspaceId, generateUUID } from '../../../lib/supabaseSync';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { C } from '../theme/participantTokens';
import { LayerBack, LayerBar } from './layout/ParticipantLayout';

interface WaitingPresenceRow {
  id: string;
  session_id: string;
  user_id: string;
  user_name: string;
  joined_at: string;
  last_ping_at: string;
}

function waitingInitials(name: string): string {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function isStalePresence(lastPingAt: string): boolean {
  try {
    return Date.now() - new Date(lastPingAt).getTime() > 60_000;
  } catch {
    return false;
  }
}

interface WaitingRoomProps {
  session: PrioritizationSession;
  userId: string;
  userName: string;
  onAdvance: () => void;
  onBack: () => void;
  /** S-3: names are hidden in this session. */
  hideNames?: boolean;
}

export function WaitingRoom({
  session,
  userId,
  userName,
  onAdvance,
  onBack,
  hideNames = false,
}: WaitingRoomProps) {
  const [presenceRows, setPresenceRows] = useState<WaitingPresenceRow[]>([]);
  const [loadingPresence, setLoadingPresence] = useState(true);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const workspaceId = getCurrentWorkspaceId() ?? session.workspaceId ?? '';

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !workspaceId || !userId) return;

    const newId = generateUUID();
    supabase
      .from('session_presence')
      .upsert(
        {
          id: newId,
          session_id: session.id,
          workspace_id: workspaceId,
          user_id: userId,
          user_name: userName || userId.slice(0, 8),
          joined_at: new Date().toISOString(),
          last_ping_at: new Date().toISOString(),
        },
        { onConflict: 'session_id,user_id', ignoreDuplicates: false },
      )
      .then(({ error }) => {
        if (error) console.warn('[WaitingRoom] upsert presence failed:', error.message);
      });

    pingIntervalRef.current = setInterval(() => {
      supabase
        .from('session_presence')
        .update({ last_ping_at: new Date().toISOString() })
        .eq('session_id', session.id)
        .eq('user_id', userId)
        .then(() => {});
    }, 20_000);

    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      supabase
        .from('session_presence')
        .delete()
        .eq('session_id', session.id)
        .eq('user_id', userId)
        .then(() => {});
    };
  }, [session.id, userId, userName, workspaceId]);

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    setLoadingPresence(true);
    supabase
      .from('session_presence')
      .select('*')
      .eq('session_id', session.id)
      .order('joined_at', { ascending: true })
      .then(({ data, error }) => {
        if (!error && data) setPresenceRows(data as WaitingPresenceRow[]);
        setLoadingPresence(false);
      });
  }, [session.id]);

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const ch = supabase
      .channel(`wr_presence:${session.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'session_presence', filter: `session_id=eq.${session.id}` },
        payload => {
          if (payload.eventType === 'INSERT') {
            const r = payload.new as WaitingPresenceRow;
            setPresenceRows(prev => (prev.some(x => x.id === r.id) ? prev : [...prev, r]));
          } else if (payload.eventType === 'UPDATE') {
            const r = payload.new as WaitingPresenceRow;
            setPresenceRows(prev => prev.map(x => (x.id === r.id ? r : x)));
          } else if (payload.eventType === 'DELETE') {
            const old = payload.old as { id: string };
            setPresenceRows(prev => prev.filter(x => x.id !== old.id));
          }
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [session.id]);

  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const ch = supabase
      .channel(`wr_events:${session.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'session_events', filter: `session_id=eq.${session.id}` },
        payload => {
          const ev = payload.new as { event_type: string };
          if (ev.event_type === 'waiting_room_started') onAdvance();
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [session.id, onAdvance]);

  const activeRows = presenceRows.filter(r => !isStalePresence(r.last_ping_at));

  return (
    <>
      <LayerBar>
        <LayerBack label="Sessions" onClick={onBack} />
        <div style={{ padding: '0 16px', fontFamily: 'Spectral, Georgia, serif', fontSize: '0.88rem', color: C.textSec, borderRight: `1px solid ${C.rule}` }}>
          {session.name}
        </div>
        <div style={{ padding: '0 14px', fontFamily: 'JetBrains Mono', fontSize: '0.52rem', color: C.mustard, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.mustard, flexShrink: 0 }} />
          Waiting Room
        </div>
      </LayerBar>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '28px 32px 48px', maxWidth: 860 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderLeft: `3px solid ${C.mustard}`, borderRadius: 4, padding: '20px 22px', marginBottom: 14 }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', fontWeight: 500, letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(212,160,23,0.7)', marginBottom: 6 }}>
              Waiting Room
            </div>
            <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.4rem', fontWeight: 600, color: C.textPri, marginBottom: 8 }}>
              {session.name}
            </div>
            <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: C.textTer, lineHeight: 1.6, margin: 0 }}>
              You&apos;re in the waiting room. The facilitator will signal when the session is ready to begin.
            </p>
            <div style={{ display: 'flex', gap: 28, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.rule}`, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1.15rem', fontWeight: 700, color: C.mustard }}>
                  {loadingPresence ? '…' : activeRows.length}
                </div>
                <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.72rem', fontWeight: 300, color: C.textTer, marginTop: 2 }}>Present now</div>
              </div>
              <div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1.15rem', fontWeight: 700, color: C.textSec }}>
                  {session.itemIds?.length ?? 0}
                </div>
                <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.72rem', fontWeight: 300, color: C.textTer, marginTop: 2 }}>Items</div>
              </div>
            </div>
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: '16px 18px', marginBottom: 10 }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.16em', color: C.textTer, textTransform: 'uppercase', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.rule}` }}>
              Present in the Room
            </div>
            {loadingPresence ? (
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.6rem', color: C.textDim }}>Loading…</div>
            ) : presenceRows.length === 0 ? (
              <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.8rem', color: C.textTer }}>No one here yet — you&apos;re the first.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {presenceRows.map(row => {
                  const isMe = row.user_id === userId;
                  const stale = isStalePresence(row.last_ping_at);
                  return (
                    <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: `1px solid ${isMe ? 'rgba(212,160,23,0.2)' : C.rule}`, borderRadius: 3, opacity: stale ? 0.5 : 1 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'JetBrains Mono', fontSize: '0.5rem', fontWeight: 700, border: `1px solid ${isMe ? 'rgba(212,160,23,0.4)' : C.ruleBright}`, color: isMe ? C.mustard : C.textDim }}>
                        {hideNames && !isMe ? '·' : waitingInitials(row.user_name || row.user_id)}
                      </div>
                      <div style={{ flex: 1, fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.8rem', color: isMe ? C.mustard : C.textSec }}>
                        {isMe ? `${row.user_name || row.user_id.slice(0, 8)} · you` : hideNames ? 'Someone in the room' : row.user_name || row.user_id.slice(0, 8)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '10px 14px',
              border: `1px solid ${C.rule}`, borderRadius: 3,
              fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.78rem', color: C.textDim,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.mustard, flexShrink: 0, animation: 'pulse 2s infinite' }} />
            Waiting for the facilitator to open the session…
          </div>
        </div>
      </div>
    </>
  );
}
